import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/gbl-logo.png';

export default function RegistrationPage() {
  const { fbUser, register, signOutUser, markRegistered, authError, setAuthError } = useAuth();
  const [form, setForm] = useState({ name: fbUser?.displayName || '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit number';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    setAuthError('');
    try {
      await register(form.name.trim(), form.phone.trim());
      markRegistered();
    } catch (err) {
      setAuthError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-slide-up">
        <div className="bg-white rounded-lg shadow-auth border border-[#DADCE0] overflow-hidden">
          <div className="p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <img src={logo} alt="GBL" className="h-8" />
              <button
                onClick={signOutUser}
                className="flex items-center gap-1.5 text-xs text-[#5F6368] hover:text-[#202124] transition-colors px-3 py-1.5 rounded-lg hover:bg-[#F1F3F4]"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </div>

            {/* Title */}
            <div className="mb-6">
              <h1 className="text-xl font-normal text-[#202124]">Register Your Interest</h1>
              <p className="text-[#5F6368] text-sm mt-1">
                No application found for <span className="font-medium text-[#202124]">{fbUser?.email}</span>.
                Fill in your details and our team will get in touch.
              </p>
            </div>

            {authError && (
              <div className="mb-4 p-3 rounded-lg bg-[#FCE8E6] border border-[#F5C6CB] text-sm text-[#D93025] flex items-start gap-2 animate-fade-in">
                <svg className="w-4 h-4 text-[#D93025] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                {authError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-1.5">
                  Full Name <span className="text-[#D93025]">*</span>
                </label>
                <input
                  className={`input ${errors.name ? 'border-[#D93025] focus:border-[#D93025]' : ''}`}
                  placeholder="Enter your full name"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                />
                {errors.name && (
                  <p className="text-xs text-[#D93025] mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#202124] mb-1.5">
                  Phone Number <span className="text-[#D93025]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#5F6368]">+91</span>
                  <input
                    className={`input ${errors.phone ? 'border-[#D93025] focus:border-[#D93025]' : ''}`}
                    style={{ paddingLeft: '3rem' }}
                    placeholder="10-digit mobile number"
                    value={form.phone}
                    maxLength={10}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '') }))}
                  />
                </div>
                {errors.phone && (
                  <p className="text-xs text-[#D93025] mt-1">{errors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#202124] mb-1.5">Email</label>
                <div className="input bg-[#F8F9FA] text-[#5F6368] cursor-not-allowed flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#5F6368] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="text-sm">{fbUser?.email}</span>
                </div>
                <p className="text-xs text-[#1E8E3E] mt-1.5 flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                  Verified via Google sign-in
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full py-3 mt-2 gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
